/**
 * Project File I/O utilities for web app
 *
 * Provides functions to download and upload .emberboard project files
 * with embedded base64 images for portability.
 */

import { imageStore } from './browserAPI';
import { blobManager } from './blobManager';
import { AspectRatio } from '@/types';

interface ProjectFileData {
  projectName: string;
  fps: number;
  aspectRatio?: AspectRatio;
  nodes: any[];
  referenceNodes?: any[]; // Reference library nodes
  noteNodes?: any[]; // Note nodes
  titleNodes?: any[]; // Title overlay nodes
  audioTracks?: any[]; // Audio tracks
  commentBoxes?: any[]; // Comment box annotations
  sceneHeadingHistory?: any[]; // Scene heading autocomplete history
  author?: string; // Project author
  description?: string; // Project description
  images: Record<string, string>; // relativePath -> base64 data URL
  createdAt: string;
  modifiedAt: string;
}

/**
 * Convert blob URL to base64 data URL
 */
async function blobUrlToBase64(blobUrl: string): Promise<string> {
  const response = await fetch(blobUrl);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Download project as a .emberboard file
 */
export async function downloadProject(
  projectName: string,
  fps: number,
  aspectRatio: AspectRatio,
  nodes: any[],
  referenceNodes?: any[],
  noteNodes?: any[],
  titleNodes?: any[],
  audioTracks?: any[],
  commentBoxes?: any[],
  author?: string,
  description?: string
): Promise<void> {
  // Collect all image paths that are actually used in the project
  const usedImagePaths = new Set<string>();

  // Add beat node images
  for (const node of nodes) {
    if (node.data?.imagePath) {
      usedImagePaths.add(node.data.imagePath);
    }
  }

  // Add reference node images
  if (referenceNodes) {
    for (const refNode of referenceNodes) {
      if (refNode.data?.imagePath) {
        usedImagePaths.add(refNode.data.imagePath);
      }
    }
  }

  console.log('[downloadProject] Used image paths:', Array.from(usedImagePaths));
  console.log('[downloadProject] imageStore size:', imageStore.size, 'blobManager count:', blobManager.getCount());

  // Convert only used images to base64
  const images: Record<string, string> = {};

  // Save images from imageStore (beat images, only if used)
  for (const [relativePath, blobUrl] of imageStore.entries()) {
    if (!usedImagePaths.has(relativePath)) {
      console.log(`[downloadProject] Skipping unused imageStore image: ${relativePath}`);
      continue;
    }

    try {
      console.log(`Converting image ${relativePath} from blob URL to base64...`);
      images[relativePath] = await blobUrlToBase64(blobUrl);
      console.log(`Successfully converted ${relativePath}`);
    } catch (error) {
      console.error(`Failed to convert image ${relativePath}:`, error);
    }
  }

  // Save images from blobManager (reference images and node images, only if used)
  for (const [key, blobUrl] of blobManager.getAll().entries()) {
    if (!usedImagePaths.has(key)) {
      console.log(`[downloadProject] Skipping unused blobManager image: ${key}`);
      continue;
    }

    try {
      console.log(`Converting blob-managed image ${key} from blob URL to base64...`);
      images[key] = await blobUrlToBase64(blobUrl);
      console.log(`Successfully converted ${key}`);
    } catch (error) {
      console.error(`Failed to convert blob-managed image ${key}:`, error);
    }
  }

  console.log('Total images converted for download:', Object.keys(images).length);

  const projectData: ProjectFileData = {
    projectName,
    fps,
    aspectRatio,
    nodes,
    referenceNodes: referenceNodes || [],
    noteNodes: noteNodes || [],
    titleNodes: titleNodes || [],
    audioTracks: audioTracks || [],
    commentBoxes: commentBoxes || [],
    sceneHeadingHistory: [],
    author,
    description,
    images,
    createdAt: new Date().toISOString(),
    modifiedAt: new Date().toISOString(),
  };

  // Create blob and download
  const jsonString = JSON.stringify(projectData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `${projectName || 'project'}.emberboard`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Upload and parse a .emberboard file
 */
export async function uploadProject(): Promise<{
  projectName: string;
  fps: number;
  aspectRatio?: AspectRatio;
  nodes: any[];
  referenceNodes?: any[];
  noteNodes?: any[];
  titleNodes?: any[];
  audioTracks?: any[];
  commentBoxes?: any[];
  sceneHeadingHistory?: any[];
  author?: string;
  description?: string;
  images: Map<string, string>;
} | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.emberboard,application/json';

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }

      try {
        const text = await file.text();
        const projectData: ProjectFileData = JSON.parse(text);

        // Convert base64 images to blob URLs
        const images = new Map<string, string>();
        console.log('Loading images from project file:', Object.keys(projectData.images || {}));

        for (const [relativePath, base64] of Object.entries(projectData.images || {})) {
          try {
            // Extract mime type and data from base64 string
            const match = base64.match(/^data:([^;]+);base64,(.+)$/);
            if (!match) {
              console.warn(`Image ${relativePath} has invalid base64 format`);
              continue;
            }

            const mimeType = match[1];
            const base64Data = match[2];

            // Convert base64 to blob
            const byteString = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const uint8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < byteString.length; i++) {
              uint8Array[i] = byteString.charCodeAt(i);
            }

            const blob = new Blob([arrayBuffer], { type: mimeType });

            // Check if this is a blob-managed image (reference images, etc.)
            // Blob-managed images have keys like "reference-image:abc123" or "beat-image:xyz789"
            if (relativePath.includes(':')) {
              // Store in blobManager
              const blobUrl = blobManager.create(blob, relativePath);
              images.set(relativePath, blobUrl);
              console.log(`Converted blob-managed image ${relativePath} to blob URL:`, blobUrl);
            } else {
              // Store in imageStore (legacy beat images with relative paths)
              const blobUrl = URL.createObjectURL(blob);
              images.set(relativePath, blobUrl);
              console.log(`Converted image ${relativePath} to blob URL:`, blobUrl);
            }
          } catch (error) {
            console.error(`Failed to convert image ${relativePath}:`, error);
          }
        }

        console.log('Total images loaded:', images.size);

        resolve({
          projectName: projectData.projectName,
          fps: projectData.fps,
          aspectRatio: projectData.aspectRatio,
          nodes: projectData.nodes,
          referenceNodes: projectData.referenceNodes || [],
          noteNodes: projectData.noteNodes || [],
          titleNodes: projectData.titleNodes || [],
          audioTracks: projectData.audioTracks || [],
          commentBoxes: projectData.commentBoxes || [],
          sceneHeadingHistory: projectData.sceneHeadingHistory || [],
          author: projectData.author,
          description: projectData.description,
          images,
        });
      } catch (error) {
        console.error('Failed to parse project file:', error);
        alert('Failed to load project file. Please make sure it is a valid .emberboard file.');
        resolve(null);
      }
    };

    input.oncancel = () => resolve(null);
    input.click();
  });
}
